// SlotsPanel — the reusable slot editor.
//
// Used in three places with one prop changing the data source:
//   <SlotsPanel scope="client"   clientId={id} />
//   <SlotsPanel scope="employee" employeeId={id} />
//   <SlotsPanel scope="all" />
//
// Same UI everywhere → identical interaction model regardless of which
// admin page you opened. Single write path (the slot mutations) keeps
// all three views in sync via Apollo cache invalidation.

import React, { useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation } from "@apollo/client";
import {
  Card, Typography, Input, Button, Modal, Form, Select, Tag, Tooltip,
  Popconfirm, Empty, Space, Avatar, message, Switch, Drawer, Divider,
} from "antd";
import {
  PlusOutlined, ReloadOutlined, SearchOutlined, DeleteOutlined,
  StopOutlined, CheckCircleOutlined, EditOutlined, UsergroupAddOutlined,
  MailOutlined, UserAddOutlined, UserDeleteOutlined,
} from "@ant-design/icons";

import {
  GET_ALL_SLOTS, GET_SLOTS_BY_CLIENT, GET_SLOTS_BY_EMPLOYEE,
  ADD_SLOT, UPDATE_SLOT, ADD_SLOT_MEMBER, REMOVE_SLOT_MEMBER,
  SET_SLOT_MEMBER_ROLE, DEACTIVATE_SLOT, REACTIVATE_SLOT, DELETE_SLOT,
} from "../../../Queries/clientSlots";
import { GET_ALL_CLIENTS, GET_ALL_USERS } from "../../../Queries/Users";

const { Title, Text } = Typography;

const ROLE_COLORS = {
  Manager: "gold",
  Member: "blue",
  Backup: "purple",
};

const initialsOf = (s) =>
  (s || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";

export default function SlotsPanel({
  scope = "all",        // "all" | "client" | "employee"
  clientId,
  employeeId,
  // When embedded in another page (e.g., Client detail), hide the heading
  // and the new-slot CTA can be controlled from outside.
  embedded = false,
}) {
  // Logged-in user — for actor audit fields
  const userFromUserSlice = useSelector((s) => s.user?.user);
  const userFromAuthSlice = useSelector((s) => s.auth?.userData);
  const currentUser = userFromUserSlice || userFromAuthSlice || {};
  const actor = useMemo(() => ({
    empid: currentUser?.id || "",
    name: (`${currentUser?.firstname || ""} ${currentUser?.lastname || ""}`.trim() ||
           currentUser?.email || ""),
  }), [currentUser]);

  // ----- pick the right query for this scope --------------------------
  const isAll = scope === "all";
  const isClient = scope === "client";
  const isEmployee = scope === "employee";

  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const queryOptions = useMemo(() => {
    if (isClient) return { query: GET_SLOTS_BY_CLIENT, variables: { clientId } };
    if (isEmployee) return { query: GET_SLOTS_BY_EMPLOYEE, variables: { employeeId } };
    return {
      query: GET_ALL_SLOTS,
      variables: {
        search: search || undefined,
        isActive: showInactive ? undefined : true,
        limit: 1000,
      },
    };
  }, [isClient, isEmployee, clientId, employeeId, search, showInactive]);

  const { data, loading, refetch } = useQuery(queryOptions.query, {
    variables: queryOptions.variables,
    fetchPolicy: "cache-and-network",
    skip:
      (isClient && !clientId) ||
      (isEmployee && !employeeId),
  });

  const slots = (
    data?.getSlotsByClient?.records ||
    data?.getSlotsByEmployee?.records ||
    data?.getAllSlots?.records ||
    []
  );

  // Employee + client pickers data (used in modals)
  const { data: clientsData } = useQuery(GET_ALL_CLIENTS, { fetchPolicy: "cache-first" });
  const { data: usersData } = useQuery(GET_ALL_USERS, { fetchPolicy: "cache-first" });
  const clients = clientsData?.getAllClients?.response || [];
  const employees = usersData?.getAllUsers?.response || [];

  const clientOptions = useMemo(
    () => clients
      .filter((c) => c?.clientName)
      .map((c) => ({ label: c.clientName + (c.agencyName ? ` — ${c.agencyName}` : ""), value: c.id, clientName: c.clientName })),
    [clients]
  );
  const employeeOptions = useMemo(
    () => employees
      .filter((e) => e?.id)
      .map((e) => ({
        label: (`${e.firstname || ""} ${e.lastname || ""}`.trim() || e.email),
        value: e.id,
      })),
    [employees]
  );
  const employeeNameById = useMemo(() => {
    const m = {};
    employees.forEach((e) => {
      m[e.id] = (`${e.firstname || ""} ${e.lastname || ""}`.trim() || e.email || e.id);
    });
    return m;
  }, [employees]);

  // ----- Mutations ----------------------------------------------------
  const [addSlot, { loading: addingSlot }] = useMutation(ADD_SLOT);
  const [updateSlot] = useMutation(UPDATE_SLOT);
  const [addMember] = useMutation(ADD_SLOT_MEMBER);
  const [removeMember] = useMutation(REMOVE_SLOT_MEMBER);
  const [setRole] = useMutation(SET_SLOT_MEMBER_ROLE);
  const [deactivateSlot] = useMutation(DEACTIVATE_SLOT);
  const [reactivateSlot] = useMutation(REACTIVATE_SLOT);
  const [deleteSlot] = useMutation(DELETE_SLOT);

  // ----- Add slot modal ------------------------------------------------
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const openAdd = useCallback(() => {
    addForm.resetFields();
    if (isClient && clientId) addForm.setFieldsValue({ clientId });
    setAddOpen(true);
  }, [addForm, isClient, clientId]);

  const submitAdd = async () => {
    try {
      const v = await addForm.validateFields();
      const { data: res } = await addSlot({
        variables: {
          input: {
            clientId: v.clientId,
            assignedEmail: v.assignedEmail.trim().toLowerCase(),
            label: (v.label || "").trim(),
            department: v.department || "",
            members: (v.members || []).map((id) => ({
              empId: id,
              empName: employeeNameById[id] || "",
              role: v.defaultRole || "Member",
            })),
            actorEmpid: actor.empid,
            actorName: actor.name,
          },
        },
      });
      const out = res?.addSlot;
      if (out?.status === 200) {
        message.success("Slot created");
        setAddOpen(false);
        addForm.resetFields();
        refetch();
      } else {
        message.error(out?.message || "Failed to create slot");
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "Failed to create slot");
    }
  };

  // ----- Manage one slot (right-side drawer) --------------------------
  const [focused, setFocused] = useState(null);

  const handleAddMember = async (slot, empId, role = "Member") => {
    const { data: res } = await addMember({
      variables: {
        slotId: slot.id,
        member: { empId, empName: employeeNameById[empId] || "", role },
        actorEmpid: actor.empid,
        actorName: actor.name,
      },
    });
    const out = res?.addSlotMember;
    if (out?.status === 200) {
      message.success("Member added");
      setFocused(out.record);
      refetch();
    } else {
      message.error(out?.message || "Failed");
    }
  };

  const handleRemoveMember = async (slot, empId) => {
    const { data: res } = await removeMember({ variables: { slotId: slot.id, empId } });
    const out = res?.removeSlotMember;
    if (out?.status === 200) {
      message.success("Member removed");
      setFocused(out.record);
      refetch();
    } else {
      message.error(out?.message || "Failed");
    }
  };

  const handleSetRole = async (slot, empId, role) => {
    const { data: res } = await setRole({ variables: { slotId: slot.id, empId, role } });
    const out = res?.setSlotMemberRole;
    if (out?.status === 200) {
      setFocused(out.record);
      refetch();
    } else {
      message.error(out?.message || "Failed");
    }
  };

  const handleUpdateSlot = async (slot, patch) => {
    const { data: res } = await updateSlot({ variables: { slotId: slot.id, input: patch } });
    const out = res?.updateSlot;
    if (out?.status === 200) {
      message.success("Slot updated");
      setFocused(out.record);
      refetch();
    } else {
      message.error(out?.message || "Failed");
    }
  };

  const handleToggleActive = async (slot) => {
    if (slot.isActive) {
      const { data: res } = await deactivateSlot({
        variables: { slotId: slot.id, actorEmpid: actor.empid, actorName: actor.name },
      });
      if (res?.deactivateSlot?.status === 200) { message.success("Deactivated"); setFocused(res.deactivateSlot.record); refetch(); }
      else message.error(res?.deactivateSlot?.message || "Failed");
    } else {
      const { data: res } = await reactivateSlot({ variables: { slotId: slot.id } });
      if (res?.reactivateSlot?.status === 200) { message.success("Reactivated"); setFocused(res.reactivateSlot.record); refetch(); }
      else message.error(res?.reactivateSlot?.message || "Failed");
    }
  };

  const handleDelete = async (slot) => {
    const { data: res } = await deleteSlot({ variables: { slotId: slot.id } });
    if (res?.deleteSlot?.status === 200) { message.success("Deleted"); setFocused(null); refetch(); }
    else message.error(res?.deleteSlot?.message || "Failed");
  };

  // ----- Group slots for rendering ------------------------------------
  // For employee view, group by client. For client view, no grouping.
  // For all view, group by client.
  const grouped = useMemo(() => {
    if (isClient) return [{ key: clientId, clientName: slots[0]?.clientName || "—", items: slots }];
    const m = new Map();
    for (const s of slots) {
      const k = s.clientId || "—";
      if (!m.has(k)) m.set(k, { key: k, clientName: s.clientName || "—", items: [] });
      m.get(k).items.push(s);
    }
    return Array.from(m.values()).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [slots, isClient, clientId]);

  // ----- Render -------------------------------------------------------
  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {isClient ? "Slots" : isEmployee ? "My Access" : "Access — Slots"}
            </Title>
            <Text type="secondary">
              {isClient && "Each slot has one email and the employees with access to it."}
              {isEmployee && "All slots you're a member of, grouped by client."}
              {isAll && "Single source of truth — edit anywhere, reflected everywhere."}
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
            {(isClient || isAll) && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                Add Slot
              </Button>
            )}
          </Space>
        </div>
      )}

      {isAll && (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Space>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by email, label, or client"
              allowClear
              style={{ width: 320 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Switch
              checked={showInactive}
              onChange={setShowInactive}
              checkedChildren="incl. inactive"
              unCheckedChildren="active only"
            />
          </Space>
        </Card>
      )}

      <Card loading={loading} bordered={!embedded}>
        {grouped.length === 0 ? (
          <Empty
            description={
              isClient
                ? "No slots yet — add one to start receiving requests for this client."
                : isEmployee
                ? "You're not in any slot yet — your admin needs to add you."
                : "No slots match your filters."
            }
          />
        ) : (
          <div>
            {grouped.map((g) => (
              <div key={g.key} style={{ marginBottom: 20 }}>
                {!isClient && (
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#1f2937" }}>
                    {g.clientName}
                    <Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
                      ({g.items.length} {g.items.length === 1 ? "slot" : "slots"})
                    </Text>
                  </div>
                )}
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  {g.items.map((slot) => (
                    <SlotRow
                      key={slot.id}
                      slot={slot}
                      employeeNameById={employeeNameById}
                      onOpen={() => setFocused(slot)}
                    />
                  ))}
                </Space>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add slot modal */}
      <Modal
        title="Add slot"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={submitAdd}
        confirmLoading={addingSlot}
        destroyOnClose
        okText="Create slot"
      >
        <Form layout="vertical" form={addForm}>
          {!(isClient && clientId) && (
            <Form.Item name="clientId" label="Client" rules={[{ required: true, message: "Pick a client" }]}>
              <Select showSearch optionFilterProp="label" placeholder="Pick a client" options={clientOptions} />
            </Form.Item>
          )}
          <Form.Item
            name="assignedEmail"
            label="Assigned email"
            rules={[
              { required: true, message: "Email required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input placeholder="e.g. acme-renewals@blueplanit.com" />
          </Form.Item>
          <Form.Item name="label" label="Label (optional)">
            <Input placeholder='e.g. "Renewals", "Claims"' />
          </Form.Item>
          <Form.Item name="department" label="Department (optional)">
            <Input placeholder='e.g. "Personal Lines"' />
          </Form.Item>
          <Form.Item name="members" label="Employees with access (optional)">
            <Select mode="multiple" showSearch optionFilterProp="label" placeholder="Pick employees" options={employeeOptions} />
          </Form.Item>
          <Form.Item name="defaultRole" label="Default role for new members" initialValue="Member">
            <Select options={[
              { label: "Manager", value: "Manager" },
              { label: "Member", value: "Member" },
              { label: "Backup", value: "Backup" },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Slot focus drawer (the per-slot detail editor) */}
      <SlotDrawer
        slot={focused}
        onClose={() => setFocused(null)}
        employeeOptions={employeeOptions}
        employeeNameById={employeeNameById}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
        onSetRole={handleSetRole}
        onUpdateSlot={handleUpdateSlot}
        onToggleActive={handleToggleActive}
        onDelete={handleDelete}
      />
    </div>
  );
}

// ---------- Slot row (compact summary, click to focus) -----------------

function SlotRow({ slot, employeeNameById, onOpen }) {
  const labelOrEmail = slot.label || slot.assignedEmail || "(no email set)";
  return (
    <div
      onClick={onOpen}
      style={{
        cursor: "pointer",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: "12px 16px",
        background: slot.isActive ? "#fff" : "#fafafa",
        display: "flex",
        alignItems: "center",
        gap: 16,
        transition: "background 0.15s, border-color 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1677ff"; e.currentTarget.style.background = "#fafcff"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#f0f0f0"; e.currentTarget.style.background = slot.isActive ? "#fff" : "#fafafa"; }}
    >
      <div
        style={{
          width: 32, height: 32, borderRadius: 8,
          background: slot.isActive ? "#e6f4ff" : "#f5f5f5",
          color: slot.isActive ? "#1677ff" : "#8c8c8c",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 600,
        }}
      >
        #{slot.slotNumber || "?"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{labelOrEmail}</span>
          {!slot.isActive && <Tag>Inactive</Tag>}
          {!slot.assignedEmail && <Tag color="warning">No email</Tag>}
          {slot.members.length === 0 && <Tag color="warning">No members</Tag>}
        </div>
        {slot.label && slot.assignedEmail && (
          <Text type="secondary" style={{ fontSize: 12 }}>{slot.assignedEmail}</Text>
        )}
      </div>
      <Avatar.Group max={{ count: 4 }} size="small">
        {(slot.members || []).map((m) => (
          <Tooltip key={m.empId} title={`${m.empName || employeeNameById[m.empId] || m.empId} · ${m.role}`}>
            <Avatar
              style={{
                backgroundColor:
                  m.role === "Manager" ? "#faad14"
                  : m.role === "Backup" ? "#722ed1"
                  : "#1677ff",
                fontSize: 12,
              }}
            >
              {initialsOf(m.empName || employeeNameById[m.empId] || m.empId)}
            </Avatar>
          </Tooltip>
        ))}
        {(slot.members || []).length === 0 ? (
          <Tag color="default" style={{ marginLeft: 4 }}>empty</Tag>
        ) : null}
      </Avatar.Group>
    </div>
  );
}

// ---------- Slot focus drawer (the per-slot editor) -------------------

function SlotDrawer({
  slot, onClose,
  employeeOptions, employeeNameById,
  onAddMember, onRemoveMember, onSetRole, onUpdateSlot, onToggleActive, onDelete,
}) {
  const [editForm] = Form.useForm();
  const [addMemberId, setAddMemberId] = useState(undefined);

  React.useEffect(() => {
    if (slot) {
      editForm.setFieldsValue({
        label: slot.label,
        assignedEmail: slot.assignedEmail,
        department: slot.department,
      });
      setAddMemberId(undefined);
    }
  }, [slot, editForm]);

  if (!slot) return null;

  const submitEdit = async () => {
    try {
      const v = await editForm.validateFields();
      const patch = {};
      if (v.label !== slot.label) patch.label = v.label;
      if (v.assignedEmail !== slot.assignedEmail) patch.assignedEmail = v.assignedEmail;
      if (v.department !== slot.department) patch.department = v.department;
      if (Object.keys(patch).length === 0) return;
      await onUpdateSlot(slot, patch);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "Failed");
    }
  };

  const memberIds = new Set((slot.members || []).map((m) => m.empId));
  const candidateEmployees = employeeOptions.filter((e) => !memberIds.has(e.value));

  return (
    <Drawer
      open={!!slot}
      onClose={onClose}
      width={520}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            Slot #{slot.slotNumber} · {slot.clientName}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {slot.assignedEmail || "no email set"}
          </Text>
        </div>
      }
    >
      <Form layout="vertical" form={editForm}>
        <Form.Item name="label" label="Label">
          <Input placeholder='e.g. "Renewals"' onBlur={submitEdit} />
        </Form.Item>
        <Form.Item name="assignedEmail" label="Assigned email" rules={[{ type: "email" }]}>
          <Input placeholder="email@blueplanit.com" onBlur={submitEdit} />
        </Form.Item>
        <Form.Item name="department" label="Department">
          <Input onBlur={submitEdit} />
        </Form.Item>
      </Form>

      <Divider>Members ({(slot.members || []).length})</Divider>

      <div style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: "100%" }}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Add an employee"
            style={{ flex: 1 }}
            value={addMemberId}
            onChange={setAddMemberId}
            options={candidateEmployees}
          />
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            disabled={!addMemberId}
            onClick={async () => {
              await onAddMember(slot, addMemberId);
              setAddMemberId(undefined);
            }}
          >
            Add
          </Button>
        </Space.Compact>
      </div>

      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        {(slot.members || []).map((m) => (
          <div
            key={m.empId}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Avatar style={{ backgroundColor: ROLE_COLORS[m.role] === "gold" ? "#faad14" : ROLE_COLORS[m.role] === "purple" ? "#722ed1" : "#1677ff", fontSize: 11 }}>
              {initialsOf(m.empName || employeeNameById[m.empId] || m.empId)}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>
                {m.empName || employeeNameById[m.empId] || m.empId}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Added {fmtDate(m.addedAt)}
                {m.addedBy?.name ? ` · by ${m.addedBy.name}` : ""}
              </Text>
            </div>
            <Select
              value={m.role}
              size="small"
              style={{ width: 110 }}
              onChange={(role) => onSetRole(slot, m.empId, role)}
              options={[
                { label: "Manager", value: "Manager" },
                { label: "Member", value: "Member" },
                { label: "Backup", value: "Backup" },
              ]}
            />
            <Tooltip title="Remove">
              <Button
                size="small"
                danger
                icon={<UserDeleteOutlined />}
                onClick={() => onRemoveMember(slot, m.empId)}
              />
            </Tooltip>
          </div>
        ))}
      </Space>

      <Divider>Slot status</Divider>
      <Space>
        {slot.isActive ? (
          <Popconfirm
            title="Deactivate slot?"
            description="Employees lose access; existing requests stay but become read-only for them."
            okText="Deactivate"
            okButtonProps={{ danger: true }}
            onConfirm={() => onToggleActive(slot)}
          >
            <Button icon={<StopOutlined />} danger>Deactivate</Button>
          </Popconfirm>
        ) : (
          <Button icon={<CheckCircleOutlined />} onClick={() => onToggleActive(slot)}>
            Reactivate
          </Button>
        )}
        <Popconfirm
          title="Permanently delete slot?"
          description="Cannot be undone. Existing requests stay but become orphaned."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(slot)}
        >
          <Button icon={<DeleteOutlined />} danger>Delete</Button>
        </Popconfirm>
      </Space>

      <Divider>Audit</Divider>
      <div style={{ fontSize: 12, color: "#8c8c8c" }}>
        <div>Created {fmtDate(slot.createdAt)}{slot.createdBy?.name ? ` · by ${slot.createdBy.name}` : ""}</div>
        {slot.deactivatedAt && (
          <div>Deactivated {fmtDate(slot.deactivatedAt)}{slot.deactivatedBy?.name ? ` · by ${slot.deactivatedBy.name}` : ""}</div>
        )}
      </div>
    </Drawer>
  );
}
